import {
    firstLineTextUnread,
    firstLineTextRead,
    mclFirstLineTextUnread,
    mclFirstLineTextUnreadTypeRamp,
    mclFirstLineTextUnreadTypeRampSelected,
    firstLineTextMedium,
    firstLineTextCompact,
    firstLineTextSCLTypeRampFull,
    firstLineTextSCLTypeRampMedium,
    firstLineTextSCLTypeRampCompact,
} from 'owa-mail-listitem-styles/lib/scss/MailListItemFirstLine.scss';
import { tokens } from '@fluentui/react-components';
import { useIsTypeRampUpdatesEnabled } from 'owa-mail-list-item-shared/lib/utils/useIsTypeRampUpdatesEnabled';
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports  -- (https://aka.ms/OWALintWiki)
 * Baseline. Do not copy and paste"
 *	> '../index' import is restricted from being used. */
import { MailListItemTypePlaceholder } from '../index';
import classnames from 'owa-classnames';
import { observer } from 'owa-mobx-react';
import { highlightTermsInHtmlElement } from 'owa-highlight';
import { useMessageListTextStyle } from 'owa-mail-listitem-styles/lib/utils/useMessageListTextStyle';
import React from 'react';
import {
    type ConditionalFormattingModification,
    getConditionalFormattingStyles,
    ConditionalFormattingSurface,
} from 'owa-conditional-formatting';
import type { Accuracy } from 'owa-mark/lib/utils/markElements';
import { getDensityModeCssClass } from 'owa-fabric-theme';

export interface MailListItemFirstLineTextProps {
    highlightTerms: string[];
    showDraftsIndicator: boolean;
    isInOutboxFolder: boolean;
    unreadCount: number;
    text: string;
    tooltipText?: string;
    isSingleLineView?: boolean;
    highlightAccuracy?: Accuracy;
    isScheduledSend?: boolean;
    isProactiveDraft?: boolean;
    conditionalFormattingModifications?: ConditionalFormattingModification[];
    isDarkTheme: boolean;
    isSelected: boolean;
    isUnread?: boolean;
}

const defaultExport: React.FunctionComponent<MailListItemFirstLineTextProps> = observer(
    function MailListItemFirstLineText(props: MailListItemFirstLineTextProps) {
        const {
            highlightTerms: _highlightTerms,
            highlightAccuracy,
            showDraftsIndicator,
            isInOutboxFolder,
            text,
            tooltipText,
            unreadCount,
            isSingleLineView = false, // Special case is for SLV, so assume false by default and require caller to opt in
            isScheduledSend,
            isProactiveDraft,
            conditionalFormattingModifications,
            isDarkTheme,
            isSelected,
            isUnread = false,
        } = props;

        const isTypeRampUpdatesEnabled = useIsTypeRampUpdatesEnabled();
        const densityModeCssClass = isTypeRampUpdatesEnabled
            ? getDensityModeCssClass(
                  firstLineTextSCLTypeRampFull,
                  firstLineTextSCLTypeRampMedium,
                  firstLineTextSCLTypeRampCompact
              )
            : getDensityModeCssClass(
                  firstLineTextMedium,
                  firstLineTextMedium,
                  firstLineTextCompact
              );

        const messageListTextStyle = useMessageListTextStyle(
            'Major',
            true /* isFirstLine */,
            isSingleLineView
        );

        const firstLineTextClasses = classnames(
            unreadCount > 0
                ? isSingleLineView
                    ? isTypeRampUpdatesEnabled
                        ? isSelected
                            ? mclFirstLineTextUnreadTypeRampSelected
                            : mclFirstLineTextUnreadTypeRamp
                        : mclFirstLineTextUnread
                    : firstLineTextUnread
                : firstLineTextRead,
            isTypeRampUpdatesEnabled && isSingleLineView
                ? densityModeCssClass
                : messageListTextStyle
        );

        const textRef = React.useRef<HTMLSpanElement>(null);
        React.useEffect(() => {
            highlightTermsInHtmlElement(
                textRef.current,
                _highlightTerms,
                false /* separateWordSearch */,
                highlightAccuracy /* highlightAccuracy */
            );
        }, [_highlightTerms, highlightAccuracy, text]);

        const textColor = React.useMemo(() => {
            if (
                conditionalFormattingModifications &&
                conditionalFormattingModifications.length > 0
            ) {
                const conditionalFormattingStyles = getConditionalFormattingStyles(
                    ConditionalFormattingSurface.Text,
                    conditionalFormattingModifications,
                    isDarkTheme,
                    isSelected
                );
                if (conditionalFormattingStyles) {
                    // For unread items in three-column view, force NeutralForeground1
                    // so the sender name stays bold/neutral on the row background.
                    if (isUnread && !isSingleLineView) {
                        return {
                            ...conditionalFormattingStyles,
                            color: tokens.colorNeutralForeground1,
                            '--cf-text-hover-color': tokens.colorNeutralForeground1,
                        } as React.CSSProperties;
                    }
                    // For read items (or single line view), use the hover variable pattern
                    // so that --cf-text-hover-color from container is applied on hover.
                    // Only set the hover color override when the rule actually provides a
                    // color (e.g. font-family-only rules have no color to override).
                    const baseColor = conditionalFormattingStyles.color;
                    return {
                        ...conditionalFormattingStyles,
                        ...(baseColor !== undefined
                            ? { color: `var(--cfHoverTextColorOverride, ${baseColor})` }
                            : undefined),
                    } as React.CSSProperties;
                }

                return conditionalFormattingStyles;
            }
            return {};
        }, [
            conditionalFormattingModifications,
            isDarkTheme,
            isSelected,
            isUnread,
            isSingleLineView,
        ]);

        return (
            <div className={firstLineTextClasses} style={textColor}>
                {showDraftsIndicator ? (
                    isScheduledSend ? (
                        <MailListItemTypePlaceholder messageType={'Scheduled'} />
                    ) : isProactiveDraft ? (
                        <MailListItemTypePlaceholder messageType={'ProactiveDraft'} />
                    ) : (
                        <MailListItemTypePlaceholder messageType={'Draft'} />
                    )
                ) : isInOutboxFolder ? (
                    <MailListItemTypePlaceholder messageType={'Queued'} />
                ) : undefined}
                <span title={tooltipText || undefined} ref={textRef}>
                    {text}
                </span>
            </div>
        );
    },
    'MailListItemFirstLineText'
);
export default defaultExport;
